import type { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faCircleExclamation } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Chip, Icon } from "@performyard/py-components";
import type { ReviewCycleReportRowFrontend, ReviewCycleReportStatus } from "@performyard/service-contracts";

type ChipColor = "primary" | "neutral" | "red" | "green" | "yellow";

type StatusDisplay = {
	label: string;
	color: ChipColor;
	icon?: IconDefinition | React.ReactElement;
	textClassName?: string;
};

// Overdue carries an icon so it is distinguishable from the same-colored Closed chip. The icon is
// pre-colored so it matches the label instead of the Chip's default red-5.
const STATUS_DISPLAY: Record<ReviewCycleReportStatus, StatusDisplay> = {
	active: { label: "In Progress", color: "primary" },
	scheduled: { label: "Scheduled", color: "yellow" },
	complete: { label: "Completed", color: "green" },
	overdue: {
		label: "Overdue",
		color: "red",
		icon: <Icon icon={faCircleExclamation} color="red-8" />,
		textClassName: "text-red-8",
	},
	closed: { label: "Closed", color: "red" },
};

type ReportValueGetterParams = ValueGetterParams<ReviewCycleReportRowFrontend>;
type ReportCellRendererParams = ICellRendererParams<ReviewCycleReportRowFrontend>;

const ChipCell = ({ label, color, icon, textClassName }: StatusDisplay) => (
	<div className="flex h-full items-center">
		<Chip label={label} color={color} size={24} icon={icon} dangerouslySetClassName={textClassName} />
	</div>
);

const StatusChipCellRenderer = (params: ReportCellRendererParams) => {
	if (!params.data?.status) return null;
	return <ChipCell {...STATUS_DISPLAY[params.data.status]} />;
};

const VisibilityChipCellRenderer = (params: ReportCellRendererParams) => {
	if (!params.data) return null;
	return <ChipCell label={params.data.visibleToSubject ? "Visible" : "Not Visible"} color="neutral" />;
};

const RedactedChipCellRenderer = (params: ReportCellRendererParams) => {
	if (!params.data) return null;
	return <ChipCell label={params.data.redacted ? "Yes" : "No"} color="neutral" />;
};

export const getReviewFormsColumns = (): ColDef<ReviewCycleReportRowFrontend>[] => [
	{
		colId: "formName",
		headerName: "Form Name",
		type: "text",
		valueGetter: (params: ReportValueGetterParams) => params.data?.form?.name ?? "",
		sortable: true,
		flex: 1,
	},
	{
		field: "subject",
		colId: "subjectName",
		headerName: "Review Subject",
		type: "employee",
		sortable: true,
		flex: 1,
	},
	{
		colId: "status",
		headerName: "Form Status",
		cellRenderer: StatusChipCellRenderer,
		valueGetter: (params: ReportValueGetterParams) =>
			params.data?.status ? STATUS_DISPLAY[params.data.status].label : "",
		sortable: true,
		flex: 1,
	},
	{
		field: "authorDueDate",
		headerName: "Author Due Date",
		type: "date",
		cellRendererParams: { dateFormat: "MMM d, yyyy" },
		sortable: true,
		flex: 1,
	},
	{
		field: "signerDueDate",
		headerName: "Signer Due Date",
		type: "date",
		cellRendererParams: { dateFormat: "MMM d, yyyy" },
		sortable: true,
		flex: 1,
	},
	{
		colId: "visibleToSubject",
		headerName: "Visible to Subject",
		cellRenderer: VisibilityChipCellRenderer,
		valueGetter: (params: ReportValueGetterParams) => (params.data?.visibleToSubject ? "Visible" : "Not Visible"),
		sortable: true,
		flex: 1,
	},
	{
		colId: "redacted",
		headerName: "Author Redaction",
		cellRenderer: RedactedChipCellRenderer,
		valueGetter: (params: ReportValueGetterParams) => (params.data?.redacted ? "Yes" : "No"),
		sortable: true,
		flex: 1,
	},
];
